import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { AccessManagerResource, AccessManagerResourceAction, AccessManagerResourceActionType } from './accessManager.models';
import { AccessManagerCfgService } from './accessManagerCfg.service';
import '../../util/string';
import { ApigeeService } from '../apigee/apigee.service';

export class AccessManagerUnaviableError extends Error {
    constructor(message: string) {
        super(message);
    }
}

export class AccessManagerUserNotFoundError extends Error {
    constructor(message: string) {
        super(message);
    }
}

export class AccessManagerCredentialsNotFoundError extends Error {
    constructor(message: string) {
        super(message)
    }
}

@Injectable({
    providedIn: 'root'
})
export class AccessManagerService {
    private mRetrievedResourceMap: { [key: number]: AccessManagerResource };
    private readonly mOnFlightRequestsMap: { [key: number]: Promise<AccessManagerResource> };

    constructor(private accessManagerCfgService: AccessManagerCfgService, private apigeeService: ApigeeService, private httpClient: HttpClient) {
        this.mRetrievedResourceMap = {};
        this.mOnFlightRequestsMap = {};
        this.apigeeService.authTokensUpdated.subscribe(() => this.mRetrievedResourceMap = {});
    }

    public async getResourcePermissions(resourceId: number): Promise<AccessManagerResource> {
        let onFlightRequest = this.mOnFlightRequestsMap[resourceId];
        if (onFlightRequest) {
            return onFlightRequest;
        } else {
            onFlightRequest = this.getResourcePermissionsRequest(resourceId);
            this.mOnFlightRequestsMap[resourceId] = onFlightRequest;
            onFlightRequest
                .then(() => this.mOnFlightRequestsMap[resourceId] = null)
                .catch(() => this.mOnFlightRequestsMap[resourceId] = null);

            return onFlightRequest;
        }
    }

    private async getResourcePermissionsRequest(resourceId: number): Promise<AccessManagerResource> {
        let requestedResourcePermissions = this.mRetrievedResourceMap[resourceId];
        if (!requestedResourcePermissions || !requestedResourcePermissions.isAlive) {
            let cfg = this.accessManagerCfgService.getCfg();

            if (resourceId < 0) {
                let defaultResourcePermissions = cfg.defaults.find(x => x.ResourceId == resourceId);
                if (defaultResourcePermissions) {
                    requestedResourcePermissions = new AccessManagerResource(defaultResourcePermissions);
                } else {
                    requestedResourcePermissions = AccessManagerResource.generateDefaultAllow(resourceId);
                }
                let flattenedResourcePermissions = this.flattenHierarchy(requestedResourcePermissions);
                let ttl = moment().valueOf() + this.accessManagerCfgService.getCfg().cacheTTL;
                flattenedResourcePermissions.forEach(x => {
                    x.setTTL(ttl);
                    this.mRetrievedResourceMap[x.ResourceId] = x
                });
            }
            else if (this.apigeeService.hasToken) {
                let tokenError = this.apigeeService.isTokenValid();
                if (tokenError == '') {
                    try {
                        let resourcePermissions = await this.httpClient.get<AccessManagerResource>(cfg.endpoint.format(resourceId.toString())).toPromise();
                        if (resourcePermissions.ResourceActions.find(x => x.ActionTypeId == AccessManagerResourceActionType.ErrorId && x.Description == "Permite todo, no existe empleado o usuario activo")) {
                            throw new AccessManagerUserNotFoundError("No tiene usuario del sistema, por favor haga una petición a ServiceDesk IT para solicitar la generación de su usuario con nif {0}.".format(this.apigeeService.userInfo.nameid));
                        }

                        let ttl = moment().valueOf() + this.accessManagerCfgService.getCfg().cacheTTL;
                        let processedResourcePermissions = this.processResourcePermissions(resourcePermissions, true);
                        let flattenedResourcePermissions = this.flattenHierarchy(processedResourcePermissions);
                        flattenedResourcePermissions.forEach(x => {
                            x.setTTL(ttl);
                            this.mRetrievedResourceMap[x.ResourceId] = x
                        });

                        requestedResourcePermissions = processedResourcePermissions;
                    } catch (error) {
                        if (error instanceof AccessManagerUserNotFoundError) {
                            throw error;
                        } else if (error.status == 400 && error.error.Message == 'No existe el recurso solicitado') {
                            let defaultResourcePermissions = cfg.defaults.find(x => x.ResourceId == resourceId);
                            if (defaultResourcePermissions) {
                                requestedResourcePermissions = new AccessManagerResource(defaultResourcePermissions);
                            } else {
                                requestedResourcePermissions = AccessManagerResource.generateDefaultAllow(resourceId);
                            }
                            let ttl = moment().valueOf() + this.accessManagerCfgService.getCfg().cacheTTL;
                            requestedResourcePermissions.setTTL(ttl);
                            this.mRetrievedResourceMap[resourceId] = requestedResourcePermissions;
                        } else if (error.status == 401 || error.status == -1) {
                            let defaultResourcePermissions = cfg.defaults.find(x => x.ResourceId == resourceId);
                            if (defaultResourcePermissions) {
                                requestedResourcePermissions = new AccessManagerResource(defaultResourcePermissions);
                            } else {
                                requestedResourcePermissions = AccessManagerResource.generateDefaultDeny(resourceId);
                            }
                            let ttl = moment().valueOf() + this.accessManagerCfgService.getCfg().cacheTTL;
                            requestedResourcePermissions.setTTL(ttl);
                            this.mRetrievedResourceMap[resourceId] = requestedResourcePermissions;
                        } else throw new AccessManagerUnaviableError("Ups tenemos problemas para obtener información de tu perfil.");
                    }
                } else {
                    throw new AccessManagerUnaviableError(tokenError);
                }
            }
            else {
                throw new AccessManagerCredentialsNotFoundError("User must be authenticated");
            }
        }

        return requestedResourcePermissions;
    }

    private processResourcePermissions(retrievedResourcePermissions: AccessManagerResource, defaultActionValue: boolean = false): AccessManagerResource {
        retrievedResourcePermissions = new AccessManagerResource(retrievedResourcePermissions);

        if (retrievedResourcePermissions) {
            let cfg = this.accessManagerCfgService.getCfg();
            let defaultResourcePermissions = cfg.defaults.find(x => x.ResourceId == retrievedResourcePermissions.ResourceId);
            if (defaultResourcePermissions) defaultResourcePermissions = new AccessManagerResource(defaultResourcePermissions);

            let newResourcePermissions = new AccessManagerResource(retrievedResourcePermissions.ResourceId, retrievedResourcePermissions.ResourceName);
            for (const actionType in AccessManagerResourceActionType) {
                let value = Number(actionType);
                if (value) {
                    let retrievedAction = retrievedResourcePermissions.ResourceActions.find(x => x.ActionTypeId == value);
                    if (retrievedAction) {
                        newResourcePermissions.ResourceActions.push(retrievedAction);
                    } else {
                        let defaultAction = defaultResourcePermissions ? defaultResourcePermissions.ResourceActions.find(x => x.ActionTypeId == value) : null;
                        if (defaultAction) {
                            newResourcePermissions.ResourceActions.push(defaultAction);
                        } else {
                            let actionValue = new AccessManagerResourceAction(value, '', defaultActionValue);
                            newResourcePermissions.ResourceActions.push(actionValue);
                        }
                    }
                }
            }
            retrievedResourcePermissions.ResourceNodes.forEach(x => newResourcePermissions.ResourceNodes.push(this.processResourcePermissions(x, defaultActionValue)));
            retrievedResourcePermissions.ModelNodes.forEach(x => newResourcePermissions.ModelNodes.push(x));

            return newResourcePermissions;
        } else {
            return null;
        }
    }

    private flattenHierarchy(resource: AccessManagerResource): AccessManagerResource[] {
        let resourceArray: AccessManagerResource[] = [];
        resourceArray.push(resource);
        resource.ResourceNodes.forEach(x => this.flattenHierarchy(x).forEach(y => resourceArray.push(y)));
        return resourceArray;
    }
}
